import React, { useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Columns } from "react-bootstrap-icons";
import {
  getCookieNameFromKey,
  getCookieValue,
  setCookieValue
} from "../manageTablePersistence";
import { putManageTableVisibility } from "../../services";

const toVisibilityMap = (columnList = []) => (
  columnList.reduce((acc, col) => {
    acc[col.key] = !!col.show;
    return acc;
  }, {})
);

const applyVisibilityMap = (columnList = [], visibilityMap = {}) => (
  columnList.map((col) => (
    Object.prototype.hasOwnProperty.call(visibilityMap, col.key)
      ? { ...col, show: !!visibilityMap[col.key] }
      : col
  ))
);

const hasVisibilityDiff = (left = [], right = []) => (
  left.length !== right.length || left.some((col, idx) => col.key !== right[idx]?.key || !!col.show !== !!right[idx]?.show)
);

const ManageTable = ({ columns, onColumnsChange, show, onToggle, storageKey }) => {
  const [internalShow, setInternalShow] = useState(false);
  const [tempColumns, setTempColumns] = useState(columns);
  const scrollContainerRef = useRef(null);
  const scrollTopRef = useRef(0);
  
  // Use external control if provided, otherwise use internal state
  const showManageTableDropdown = show !== undefined ? show : internalShow;
  const handleToggle = onToggle || (() => setInternalShow(!internalShow));
  const resolvedStorageKey = React.useMemo(() => {
    if (storageKey) return storageKey;
    const path = window?.location?.pathname || "default";
    let userKey = "guest";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      userKey = user?.perf_user_id || user?.id || user?._id || user?.name || "guest";
    } catch (_e) {
      userKey = "guest";
    }
    return `manage-table:${path}:${userKey}`;
  }, [storageKey]);

  React.useEffect(() => {
    const cookieName = getCookieNameFromKey(resolvedStorageKey);
    let rawValue = getCookieValue(cookieName);
    if (!rawValue) {
      try {
        const legacy = window.sessionStorage?.getItem(resolvedStorageKey);
        if (legacy) {
          setCookieValue(cookieName, legacy);
          window.sessionStorage.removeItem(resolvedStorageKey);
          rawValue = legacy;
        }
      } catch (_e) {
        /* ignore */
      }
    }

    if (!rawValue) {
      setTempColumns(columns);
      return;
    }

    try {
      const parsedVisibilityMap = JSON.parse(rawValue);
      const mergedColumns = applyVisibilityMap(columns, parsedVisibilityMap);
      setTempColumns(mergedColumns);
      if (hasVisibilityDiff(columns, mergedColumns)) {
        onColumnsChange(mergedColumns);
      }
    } catch (_e) {
      setTempColumns(columns);
    }
  }, [columns, onColumnsChange, resolvedStorageKey]);

  const handleColumnToggle = (columnKey) => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = tempColumns.map(col => 
      col.key === columnKey ? { ...col, show: !col.show } : col
    );
    setTempColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const handleDone = () => {
    const visibilityMap = toVisibilityMap(tempColumns);
    const visibilityMapString = JSON.stringify(visibilityMap);
    setCookieValue(getCookieNameFromKey(resolvedStorageKey), visibilityMapString);
    if (localStorage.getItem("token")) {
      putManageTableVisibility(resolvedStorageKey, visibilityMap).catch(() => {});
    }
    onColumnsChange(tempColumns);
    if (onToggle) {
      onToggle(false);
    } else {
      setInternalShow(false);
    }
  };

  const handleCancel = () => {
    setTempColumns(columns);
    if (onToggle) {
      onToggle(false);
    } else {
      setInternalShow(false);
    }
  };

  const handleDeselectAll = () => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = tempColumns.map((col) => ({ ...col, show: false }));
    setTempColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const handleSelectAll = () => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = tempColumns.map((col) => ({ ...col, show: true }));
    setTempColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const handleSectionToggle = (sectionTitle, selectAll) => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = tempColumns.map((col) => {
      const colSection = col.section || 'Other';
      if (colSection !== sectionTitle) return col;
      return { ...col, show: selectAll };
    });
    setTempColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const columnGroups = React.useMemo(() => {
    const hasSections = (tempColumns || []).some((col) => col?.section);
    if (!hasSections) {
      return [{ title: null, columns: tempColumns || [] }];
    }
    const sectionOrder = [];
    const bySection = new Map();
    (tempColumns || []).forEach((col) => {
      const title = col.section || 'Other';
      if (!bySection.has(title)) {
        bySection.set(title, []);
        sectionOrder.push(title);
      }
      bySection.get(title).push(col);
    });
    return sectionOrder.map((title) => ({
      title,
      columns: bySection.get(title) || [],
    }));
  }, [tempColumns]);

  const customManageTableMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <h6>Manage Table Columns</h6>
          <div style={{ marginBottom: '8px', display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={handleSelectAll}
            >
              Select All
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={handleDeselectAll}
            >
              Deselect All
            </button>
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="manage-table-columns-wrapper">
              <div className="manage-table-columns-scroll" ref={scrollContainerRef}>
                {columnGroups.map((group) => {
                  const sectionKeys = group.columns.map((c) => c.key).join('-') || 'ungrouped';
                  const allChecked = group.columns.length > 0 && group.columns.every((c) => c.show);
                  const someChecked = group.columns.some((c) => c.show);
                  return (
                    <div key={group.title || 'ungrouped'} className="manage-table-column-group">
                      {group.title && (
                        <div className="manage-table-section-title">
                          <span>{group.title}</span>
                          <label
                            className="manage-table-section-select-all"
                            htmlFor={`section-select-all-${sectionKeys}`}
                            title={`Select all in ${group.title}`}
                          >
                            <input
                              type="checkbox"
                              id={`section-select-all-${sectionKeys}`}
                              checked={allChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = someChecked && !allChecked;
                              }}
                              onChange={(e) => handleSectionToggle(group.title, e.target.checked)}
                            />
                            <span>Select All</span>
                          </label>
                        </div>
                      )}
                      <div className="manage-table-column-options">
                        {group.columns.map((column) => (
                          <div key={column.key} className="manage-table-column-option">
                            <input
                              type="checkbox"
                              id={`column-${column.key}`}
                              checked={column.show}
                              onChange={() => handleColumnToggle(column.key)}
                            />
                            <label htmlFor={`column-${column.key}`}>
                              {column.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={handleCancel}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={handleDone}> Done </button>
            </div>
          </div>
        </div>
      );
    },
  );

  return (
    <Dropdown
      key={'manage-table'}
      id="manage-table"
      className="me-2"
      show={showManageTableDropdown}
      onToggle={handleToggle}
      autoClose={false}
    > 
      <Dropdown.Toggle variant="outline-secondary">
        <Columns size={16} className="me-2"></Columns>Manage Table
      </Dropdown.Toggle>
      <Dropdown.Menu as={customManageTableMenu}/> 
    </Dropdown>
  );
};

export default ManageTable; 